import { useAppDispatch, useAppSelector } from '../../store/reduxHooks';
import {
  resetTechnicalError,
  setTechnicalErrorModal
} from '../../store/technicalError/technicalError';
import { Button, Modal } from 'react-bootstrap';
import { QueryParamsTable } from './components/QueryParamsTable/QueryParamsTable';
import { PayloadTable } from './components/PayloadTable/PayloadTable';
import { RawResponseViewer } from './components/RawResponseViewer/RawResponseViewer';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { toast } from 'react-toastify';
import './technicalErrorModal.css';
import cn from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { isTechnicalErrorInfo } from '../../utils/typeguards/isTechnicalErrorInfo';
import { UserInfo } from '../PageWrapper/components/UserInfo/UserInfo';

export const TechnicalErrorModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isErrorModal, errors } = useAppSelector(
    (state) => state.technicalError
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  const dateTimeString = now.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  useEffect(() => {
    if (!isErrorModal || errors.length === 0) setCurrentIndex(0);
    else setCurrentIndex(0);
  }, [isErrorModal, errors.length]);

  const reversedErrors = [...errors].reverse();
  const error = reversedErrors[currentIndex] ?? null;

  const handleCopyRaw = useCallback(async (): Promise<void> => {
    const data = error?.response?.data;
    if (data === undefined || data === null) {
      toast.error('Нет данных для копирования!');
      return;
    }
    let content = '';
    if (typeof data === 'string') {
      content = data;
    } else {
      try {
        content = JSON.stringify(data, null, 2);
      } catch {
        content = String(data);
      }
    }
    const ok = await copyToClipboard(content);
    if (ok) {
      toast.success('Raw response скопирован в буфер обмена браузера!', {
        className: 'toast-copy-success'
      });
    } else {
      toast.error('Не удалось скопировать!');
    }
  }, [error]);

  const handleClose = (): void => {
    dispatch(setTechnicalErrorModal(false));
    dispatch(resetTechnicalError());
  };

  if (error === null) return null;

  const rawData = error.response.data;
  const data = isTechnicalErrorInfo(rawData) ? rawData : {};

  const url = error.request.url ?? '';
  let endpoint = '';
  const method = error.request.method?.toUpperCase() ?? '';

  try {
    const urlObj = new URL(url);
    endpoint = urlObj.pathname;
  } catch {
    endpoint = url;
  }

  return (
    <Modal show={isErrorModal} onHide={handleClose} centered fullscreen>
      <Modal.Header closeButton className="error-modal__header">
        <div className="error-modal__header-content">
          <UserInfo />
          <div className="error-modal__datetime">{dateTimeString}</div>
          <Modal.Title className="error-modal__title">
            Техническая ошибка
            <div className="error-modal__hint">
              Для ускорения устранения проблемы сделайте скриншот данного экрана
              и отправьте в отдел разработки.
            </div>
            {errors.length > 1 && (
              <div className="error-modal__multi">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="me-2"
                  disabled={currentIndex === 0}
                  onClick={() => setCurrentIndex((i) => Math.max(i - 1, 0))}
                >
                  ←
                </Button>
                <span>
                  Ошибка {currentIndex + 1} из {errors.length}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  disabled={currentIndex === errors.length - 1}
                  onClick={() =>
                    setCurrentIndex((i) => Math.min(i + 1, errors.length - 1))
                  }
                >
                  →
                </Button>
              </div>
            )}
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="error-modal__body">
        <div className="error-modal__info">
          <div>
            <span className="error-modal__label">Endpoint:</span>
            <div>
              <span
                className={cn('error-modal__method', {
                  'error-modal__method--get': method === 'GET',
                  'error-modal__method--post': method === 'POST',
                  'error-modal__method--delete': method === 'DELETE',
                  'error-modal__method--put': method === 'PUT'
                })}
              >
                {method} : {endpoint}
              </span>
            </div>
          </div>
          <div>
            <span className="error-modal__label">Error message:</span>
            <div>
              <span className="error-modal__value-bg">
                {error.response.message}
              </span>
            </div>
          </div>
          <div>
            <span className="error-modal__label">URL:</span>
            <div>
              <span className="error-modal__value-bg">{url}</span>
            </div>
          </div>
          {data.detail !== null && data.detail !== undefined && (
            <div>
              <span className="error-modal__label">DETAILS:</span>
              <pre className="error-modal__value-bg">{data.detail}</pre>
            </div>
          )}
          <div className="error-modal__row">
            {error.response.status !== undefined &&
              error.response.status !== null && (
                <div className="error-modal__item">
                  <span className="error-modal__label">Status:</span>
                  <span className="error-modal__value-bg error-modal__status">
                    {error.response.status}
                  </span>
                </div>
              )}
            {error.response.statusText !== undefined &&
              error.response.statusText !== null &&
              error.response.statusText !== '' && (
                <div className="error-modal__item">
                  <span className="error-modal__label">StatusText:</span>
                  <span className="error-modal__value-bg">
                    {error.response.statusText}
                  </span>
                </div>
              )}
            {data.title !== null && data.title !== undefined && (
              <div className="error-modal__item">
                <span className="error-modal__label">Title:</span>
                <span className="error-modal__value-bg">{data.title}</span>
              </div>
            )}
            {data.class !== null && data.class !== undefined && (
              <div className="error-modal__item">
                <span className="error-modal__label">Class:</span>
                <span className="error-modal__value-bg">{data.class}</span>
              </div>
            )}
          </div>
          <div className="error-modal__params-row">
            {typeof error.request.params === 'object' &&
              error.request.params !== null &&
              Object.keys(error.request.params).length > 0 && (
                <div className="error-modal__params">
                  <div className="error-modal__label">Параметры запроса</div>
                  <QueryParamsTable params={error.request.params} />
                </div>
              )}
            {error.request.data !== undefined &&
              error.request.data !== null && (
                <div className="error-modal__params">
                  <div className="error-modal__label">Payload</div>
                  <PayloadTable data={error.request.data} />
                </div>
              )}
          </div>
        </div>
        <div className="error-modal__raw">
          {error.networkError === false && (
            <div className="error-modal__raw-label">
              Raw Response
              <Button
                variant="success"
                size="sm"
                className="error-modal__copy-btn"
                onClick={handleCopyRaw}
                title="Скопировать в буфер"
                type="button"
              >
                Копировать
              </Button>
            </div>
          )}
          {error.networkError === true ? (
            <div className="error-modal__network">
              <strong>Network Error:</strong> Сервер не вернул ответ.
              <ul className="error-modal__network-list">
                <li>
                  Слишком большой файл (например, превышен лимит на сервере или
                  прокси)
                </li>
                <li>Проблемы с сетью или соединением</li>
                <li>Ограничения или ошибки на стороне прокси/балансировщика</li>
                <li>Сервер не отвечает или разорвал соединение</li>
              </ul>
              <div className="error-modal__network-hint">
                Проверьте размер файла, стабильность соединения и настройки
                сервера.
              </div>
            </div>
          ) : (
            <RawResponseViewer data={error.response.data} />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="error-modal__footer">
        <button className="btn btn-secondary" onClick={handleClose}>
          Закрыть
        </button>
      </Modal.Footer>
    </Modal>
  );
};
